import { api } from '@/lib/api';
import type {
  V4SubscriptionsResponse,
  V4SubscriptionDetailResponse,
  V4SubscriptionInvoicesResponse,
  V4SubscriptionsFilters,
  CreateSubscriptionV4Input,
  UpdateSubscriptionV4Input,
  SubscriptionSensor,
  AssignInvoicesResponse,
  UnassignInvoiceResponse,
  V4SubscriptionQuotesResponse,
  AssignQuotesResponse,
  UnassignQuoteResponse,
  SubscriptionManagerCandidate,
  RenewalSummary,
  ReconciliationLists,
  PortfolioTotals,
  RevenueByMonthResponse,
  RenewalQuotePreview,
  CreatedRenewalQuote,
} from './types';
import type {
  SubscriptionLine,
  SubscriptionLineInput,
  SubscriptionLineUpdate,
  SubscriptionCoveredAccount,
} from './lineTypes';

// =============================================================================
// Subscriptions V4 (PostgreSQL)
// =============================================================================

export async function getSubscriptions(filters: V4SubscriptionsFilters = {}): Promise<V4SubscriptionsResponse> {
  const params: Record<string, string | number> = {};

  if (filters.search) params.search = filters.search;
  if (filters.accountId) params.accountId = filters.accountId;
  if (filters.status) params.status = filters.status;
  if (filters.salesManagerId) params.salesManagerId = filters.salesManagerId;
  if (filters.startDateFrom) params.startDateFrom = filters.startDateFrom;
  if (filters.startDateTo) params.startDateTo = filters.startDateTo;
  if (filters.endDateFrom) params.endDateFrom = filters.endDateFrom;
  if (filters.endDateTo) params.endDateTo = filters.endDateTo;

  // Sort mapping (frontend uses snake_case sort keys, V4 uses camelCase)
  if (filters.sortKey) {
    const sortKeyMap: Record<string, string> = {
      contract_number: 'subscriptionNumber',
      name: 'name',
      status: 'status',
      account: 'accountName',
      start_date: 'startDate',
      end_date: 'endDate',
      created_at: 'createdAt',
    };
    params.sortKey = sortKeyMap[filters.sortKey] || filters.sortKey;
  }

  if (filters.sortDirection !== undefined) params.sortDirection = filters.sortDirection;
  if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
  if (filters.pageLength !== undefined) params.pageLength = filters.pageLength;

  return api.get<V4SubscriptionsResponse>('/v4/subscriptions', params);
}

/** @deprecated Use getSubscriptions */
export const getContracts = getSubscriptions;

export async function getSubscription(id: string): Promise<V4SubscriptionDetailResponse> {
  return api.get<V4SubscriptionDetailResponse>(`/v4/subscriptions/${id}`);
}

/** @deprecated Use getSubscription */
export const getContract = getSubscription;

export async function getSubscriptionInvoices(subscriptionId: string): Promise<V4SubscriptionInvoicesResponse> {
  return api.get<V4SubscriptionInvoicesResponse>(`/v4/subscriptions/${subscriptionId}/invoices`);
}

/** @deprecated Use getSubscriptionInvoices */
export const getContractInvoices = getSubscriptionInvoices;

export async function createSubscription(data: CreateSubscriptionV4Input): Promise<{ subscription: { id: string } }> {
  return api.post('/v4/subscriptions', data);
}

/** @deprecated Use createSubscription */
export const createContract = createSubscription;

export async function updateSubscription(id: string, data: UpdateSubscriptionV4Input): Promise<{ subscription: { id: string } }> {
  return api.put(`/v4/subscriptions/${id}`, data);
}

/** @deprecated Use updateSubscription */
export const updateContract = updateSubscription;

export async function deleteSubscription(id: string): Promise<{ success: boolean }> {
  return api.delete(`/v4/subscriptions/${id}`);
}

/** @deprecated Use deleteSubscription */
export const deleteContract = deleteSubscription;

export async function updateSubscriptionStatus(id: string, status: string): Promise<{ subscription: { id: string; status: string } }> {
  return api.patch(`/v4/subscriptions/${id}/status`, { status });
}

/** @deprecated Use updateSubscriptionStatus */
export const updateContractStatus = updateSubscriptionStatus;

export async function assignInvoicesToSubscription(subscriptionId: string, invoiceIds: string[]): Promise<AssignInvoicesResponse> {
  return api.post(`/v4/subscriptions/${subscriptionId}/invoices`, { invoiceIds });
}

/** @deprecated Use assignInvoicesToSubscription */
export const assignInvoicesToContract = assignInvoicesToSubscription;

export async function unassignInvoiceFromSubscription(subscriptionId: string, invoiceId: string): Promise<UnassignInvoiceResponse> {
  return api.delete(`/v4/subscriptions/${subscriptionId}/invoices/${invoiceId}`);
}

/** @deprecated Use unassignInvoiceFromSubscription */
export const unassignInvoiceFromContract = unassignInvoiceFromSubscription;

export async function searchInvoicesForSubscription(params: {
  search?: string;
  accountId: string;
  pageNumber?: number;
  pageLength?: number;
}): Promise<{ invoices: Array<{ id: string; invoiceNumber: string; status: string; total: string; currency: string; invoiceDate: string | null; subscriptionId: string | null }>; total: number }> {
  const query: Record<string, string | number> = {
    accountId: params.accountId,
    pageLength: params.pageLength ?? 100,
    pageNumber: params.pageNumber ?? 0,
  };
  if (params.search) query.search = params.search;
  return api.get('/v4/invoices', query);
}

/** @deprecated Use searchInvoicesForSubscription */
export const searchInvoicesForContract = searchInvoicesForSubscription;

export async function getSubscriptionQuotes(subscriptionId: string): Promise<V4SubscriptionQuotesResponse> {
  return api.get<V4SubscriptionQuotesResponse>(`/v4/subscriptions/${subscriptionId}/quotes`);
}

export async function assignQuotesToSubscription(subscriptionId: string, quoteIds: string[]): Promise<AssignQuotesResponse> {
  return api.post(`/v4/subscriptions/${subscriptionId}/quotes`, { quoteIds });
}

export async function unassignQuoteFromSubscription(subscriptionId: string, quoteId: string): Promise<UnassignQuoteResponse> {
  return api.delete(`/v4/subscriptions/${subscriptionId}/quotes/${quoteId}`);
}

export async function searchQuotesForSubscription(params: {
  search?: string;
  accountId: string;
  pageNumber?: number;
  pageLength?: number;
}): Promise<{ quotes: Array<{ id: string; quoteNumber: string; status: string; total: string; currency: string; subscriptionId: string | null }>; total: number }> {
  const query: Record<string, string | number> = {
    accountId: params.accountId,
    pageLength: params.pageLength ?? 100,
    pageNumber: params.pageNumber ?? 0,
  };
  if (params.search) query.search = params.search;
  return api.get('/v4/quotes', query);
}

// =============================================================================
// Subscription Sensors (PostgreSQL)
// =============================================================================

export async function getSensorsBySubscription(subscriptionNumber: string): Promise<{ sensors: SubscriptionSensor[]; total: number }> {
  return api.get(`/v4/sensors/by-subscription/${subscriptionNumber}`);
}

export async function addSensorToSubscription(sensorId: string, subscriptionNumber: string): Promise<void> {
  return api.post(`/v4/sensors/${sensorId}/add-subscription`, { subscriptionNumber });
}

export async function removeSensorFromSubscription(sensorId: string, subscriptionNumber: string): Promise<void> {
  return api.post(`/v4/sensors/${sensorId}/remove-subscription`, { subscriptionNumber });
}

// =============================================================================
// Renewal (read-only)
// =============================================================================

export async function getRenewalSummary(subscriptionId: string): Promise<RenewalSummary> {
  return api.get<RenewalSummary>(`/v4/subscriptions/${subscriptionId}/renewal-summary`);
}

export async function getReconciliation(subscriptionId: string): Promise<ReconciliationLists> {
  return api.get<ReconciliationLists>(`/v4/subscriptions/${subscriptionId}/reconciliation`);
}

export async function addSubscriptionSensor(subscriptionId: string, sensorId: string): Promise<{ added: boolean }> {
  return api.post(`/v4/subscriptions/${subscriptionId}/sensors`, { sensorId });
}

export interface BulkAddSensorsResult {
  added: number;
  addedSensorIds: string[];
  skippedSensorIds: string[];
}

export async function addAllSubscriptionSensors(subscriptionId: string, sensorIds: string[]): Promise<BulkAddSensorsResult> {
  return api.post(`/v4/subscriptions/${subscriptionId}/sensors/bulk`, { sensorIds });
}

export async function removeSubscriptionSensor(subscriptionId: string, sensorId: string): Promise<{ removed: boolean }> {
  return api.delete(`/v4/subscriptions/${subscriptionId}/sensors/${sensorId}`);
}

export async function getPortfolioTotals(): Promise<PortfolioTotals> {
  return api.get<PortfolioTotals>('/v4/subscriptions/portfolio-totals');
}

/** "Expected revenue by month" buckets (#1409). `from`/`to` are YYYY-MM, `to >= from`. */
export async function getRevenueByMonth(params: {
  from: string;
  to: string;
  salesManagerId?: string;
}): Promise<RevenueByMonthResponse> {
  const query: Record<string, string> = { from: params.from, to: params.to };
  if (params.salesManagerId) query.salesManagerId = params.salesManagerId;
  return api.get<RevenueByMonthResponse>('/v4/subscriptions/revenue-by-month', query);
}

export async function getRenewalQuotePreview(subscriptionId: string): Promise<RenewalQuotePreview> {
  return api.get<RenewalQuotePreview>(`/v4/subscriptions/${subscriptionId}/renewal-quote`, { preview: 'true' });
}

export async function createRenewalQuote(subscriptionId: string): Promise<CreatedRenewalQuote> {
  return api.post(`/v4/subscriptions/${subscriptionId}/renewal-quote`, {});
}

// Smart-picker companion: confirm the proposal by tagging the newest quote as
// recurring. `dealType` is whitelisted on PUT /v4/quotes/:id — no new endpoint.
export async function retagQuoteAsRecurring(quoteId: string): Promise<{ quote: { id: string; dealType: string } }> {
  return api.put(`/v4/quotes/${quoteId}`, { dealType: 'recurring' });
}

// =============================================================================
// Subscription lines & covered accounts
// =============================================================================

export async function addSubscriptionLine(subscriptionId: string, data: SubscriptionLineInput): Promise<{ line: SubscriptionLine }> {
  return api.post(`/v4/subscriptions/${subscriptionId}/lines`, data);
}

export async function updateSubscriptionLine(subscriptionId: string, lineId: string, data: SubscriptionLineUpdate): Promise<{ line: SubscriptionLine }> {
  return api.put(`/v4/subscriptions/${subscriptionId}/lines/${lineId}`, data);
}

export async function verifySubscriptionLine(subscriptionId: string, lineId: string): Promise<{ line: SubscriptionLine }> {
  return api.post(`/v4/subscriptions/${subscriptionId}/lines/${lineId}/verify`, {});
}

export async function addCoveredAccount(subscriptionId: string, accountId: string, reason: string): Promise<{ coveredAccount: SubscriptionCoveredAccount }> {
  return api.post(`/v4/subscriptions/${subscriptionId}/covered-accounts`, { accountId, reason });
}

// api.delete sends no body, so the account id travels in the path.
export async function removeCoveredAccount(subscriptionId: string, accountId: string): Promise<{ removed: boolean }> {
  return api.delete(`/v4/subscriptions/${subscriptionId}/covered-accounts/${accountId}`);
}

// =============================================================================
// Accounts (for dropdown)
// =============================================================================

export async function getAccountsForSelect(search?: string): Promise<{ accounts: Array<{ id: string; name: string; accountNumber: string }> }> {
  return api.get('/v4/accounts', { pageLength: 100, sortKey: 'name', sortDirection: 1, search: search || undefined });
}

// =============================================================================
// Sales Users (for dropdown) — V4 (PostgreSQL) only
// =============================================================================

export async function getSalesUsers() {
  // ATEK staff = employees + contractors. No Mongo organisation scope.
  // The option id is the V2 user id (pg_id): subscriptions store it in owner_id.
  const response = await api.get<{
    users: Array<{ pg_id: string; firstname?: string; lastname?: string; email?: string }>;
    total: number;
  }>('/v4/users', {
    employment_type: 'employee,contractor',
    enabled: 'true',
    is_subaccount: 'false',
    pageLength: 100,
  });
  return {
    users: (response.users || []).map((u) => ({
      _id: u.pg_id,
      firstname: u.firstname,
      lastname: u.lastname,
      email: u.email,
    })),
    total: response.total,
  };
}

/**
 * Match the V1 manager pickers: users whose organisation profile carries the given
 * tag for this account's Mongo organisation. The tag is the only thing that differs
 * between the contractual and technical pickers, so both go through here.
 */
async function getManagerCandidatesByTag(
  accountId: string,
  tag: 'Contractual Manager' | 'Technical Manager',
): Promise<SubscriptionManagerCandidate[]> {
  const { account } = await api.get<{
    account: { mongoOrganisationId?: string | null };
  }>(`/v4/accounts/${accountId}`);

  if (!account.mongoOrganisationId) return [];

  const response = await api.get<{
    users: Array<{
      _id: string;
      firstname?: string;
      lastname?: string;
      email?: string;
      enabled?: boolean;
    }>;
  }>('/v4/legacy/users', {
    organisation: account.mongoOrganisationId,
    tags_in: tag,
    pageLength: 100,
    sortKey: 'firstname',
    sortDirection: 1,
  });

  return (response.users || []).map((user) => ({
    id: user._id,
    name: [user.firstname, user.lastname].filter(Boolean).join(' ').trim()
      || user.email
      || user._id,
    email: user.email || null,
    enabled: user.enabled !== false,
  }));
}

export function getContractualManagerCandidates(
  accountId: string,
): Promise<SubscriptionManagerCandidate[]> {
  return getManagerCandidatesByTag(accountId, 'Contractual Manager');
}

export function getTechnicalManagerCandidates(
  accountId: string,
): Promise<SubscriptionManagerCandidate[]> {
  return getManagerCandidatesByTag(accountId, 'Technical Manager');
}
